// Template management shared by EduSense Content Studio and the Saju project.
// It knows nothing about a document format: callers pass the built-in families
// (read-only presets) and a codec that serializes, parses and instantiates
// their documents. Storage is an adapter, so the same library runs on the
// studio's IndexedDB or in memory for tests, optionally joined by the creator's
// account library on the server (private or shared with every creator).

export class TemplateLibraryError extends Error {
  constructor(code, message) {
    super(message);
    this.name = 'TemplateLibraryError';
    this.code = code;
  }
}

const TITLE_LIMIT = 200;
// An account or shared template is one server document (MongoDB, 16 MiB) holding the whole body; a longer
// body is kept on this device (StudioTemplateController). A Saju book imported while signed in keeps its
// pictures in the studio asset store (specs/012), so even the 139-page book is 7.57 MiB of Markdown and fits.
export const SERVER_TEMPLATE_BYTES = 16 * 1024 * 1024 - 64 * 1024;
const SERVER_TOO_LARGE = '서버 템플릿은 16MiB까지 저장할 수 있습니다. 이 기기에 저장해 주세요. 편집 내용은 유지됩니다.';
function forServer(markdown) {
  if (new TextEncoder().encode(markdown).byteLength > SERVER_TEMPLATE_BYTES) throw new TemplateLibraryError('TOO_LARGE', SERVER_TOO_LARGE);
  return markdown;
}
const byUpdatedDesc = (a, b) => b.updatedAt.localeCompare(a.updatedAt);

/** In-memory store with the same contract as the IndexedDB store. */
export function createMemoryTemplateStore(initial = []) {
  const records = new Map(initial.map(record => [record.id, { ...record }]));
  return {
    async list() { return [...records.values()].map(record => ({ ...record })); },
    async add(record) {
      if (records.has(record.id)) throw new TemplateLibraryError('DUPLICATE', '같은 ID의 템플릿이 이미 있습니다.');
      records.set(record.id, { ...record });
      return { ...record };
    },
    async remove(id) { records.delete(id); },
  };
}

/** IndexedDB store; `openDatabase` resolves an IDBDatabase that has `storeName`. */
export function createIndexedDbTemplateStore({ openDatabase, storeName = 'templates' }) {
  const run = async (mode, work, message) => {
    const db = await openDatabase();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(storeName, mode);
      const request = work(tx.objectStore(storeName));
      tx.oncomplete = () => { db.close(); resolve(request?.result); };
      tx.onabort = tx.onerror = () => { db.close(); reject(new TemplateLibraryError('STORAGE', message)); };
    });
  };
  return {
    list: () => run('readonly', store => store.getAll(), '저장한 템플릿 목록을 읽지 못했습니다.'),
    async add(record) {
      await run('readwrite', store => store.add(record), '템플릿을 저장하지 못했습니다. 저장 공간을 확인해 주세요.');
      return record;
    },
    async remove(id) { await run('readwrite', store => store.delete(id), '템플릿을 삭제하지 못했습니다.'); },
  };
}

export const TEMPLATE_SCOPES = Object.freeze(['device', 'account', 'shared']);

/**
 * families: [{ id, label, description?, examples: [{ id, label }] }]
 * create(familyId, exampleId) builds a fresh built-in document.
 * codec: { serialize(document) -> string, parse(string) -> document, instantiate(document) -> document }
 * store: this device's records. remote (optional): the creator's account —
 *   { available(), list() -> [{ id, title, visibility, mine, updatedAt }],
 *     get(id) -> { ...summary, markdown }, add({ id, title, markdown, visibility }),
 *     update(id, { visibility }) -> summary, remove(id) }
 * Saved templates carry `location`: device | account (private to me) | shared.
 */
export function createTemplateLibrary({ families, create, codec, store, remote = null,
  now = () => new Date().toISOString(), newId = () => crypto.randomUUID() }) {
  if (!Array.isArray(families) || typeof create !== 'function' || !codec || !store) {
    throw new TypeError('families, create, codec, store 가 모두 필요합니다.');
  }
  const builtins = families.flatMap(family => family.examples.map(example => Object.freeze({
    kind: 'builtin', id: example.id, familyId: family.id, familyLabel: family.label, title: example.label,
  })));
  const seen = new Set();
  for (const entry of builtins) {
    if (seen.has(entry.id)) throw new TypeError(`내장 템플릿 ID가 중복됩니다: ${entry.id}`);
    seen.add(entry.id);
  }

  let remoteStatus = { connected: false, error: null };
  const fromRemote = ({ id, title, visibility, mine, updatedAt, markdown }) => ({
    id, title, updatedAt, location: visibility === 'shared' ? 'shared' : 'account', mine: mine === true,
    ...(markdown === undefined ? {} : { markdown }),
  });
  const localRecords = async () => (await store.list()).map(record => ({ ...record, location: 'device', mine: true }));
  const remoteRecords = async () => {
    if (!remote) return [];
    try {
      if (!(await remote.available())) { remoteStatus = { connected: false, error: null }; return []; }
      const rows = (await remote.list()).map(fromRemote);
      remoteStatus = { connected: true, error: null };
      return rows;
    } catch (reason) {
      // The account library is optional: this device's templates stay usable.
      remoteStatus = { connected: false, error: reason instanceof Error ? reason.message : String(reason) };
      return [];
    }
  };
  const listSaved = async () => [...await localRecords(), ...await remoteRecords()].sort(byUpdatedDesc);
  const findLocal = async (id) => (await store.list()).find(item => item.id === id);
  const requireRemote = async () => {
    if (!remote || !(await remote.available())) {
      throw new TemplateLibraryError('SIGN_IN_REQUIRED', '계정에 저장하거나 공유하려면 EduSense 계정으로 로그인해 주세요.');
    }
    return remote;
  };
  const findSaved = async (id) => {
    const local = await findLocal(id);
    if (local) return { ...local, location: 'device', mine: true };
    if (remote && await remote.available().catch(() => false)) {
      const found = await remote.get(id).catch(reason => { if (reason?.code === 'NOT_FOUND') return null; throw reason; });
      if (found) return fromRemote(found);
    }
    throw new TemplateLibraryError('NOT_FOUND', '저장한 템플릿이 없습니다.');
  };

  return {
    families: Object.freeze(families.map(({ id, label, description }) => Object.freeze({ id, label, description }))),
    listBuiltins({ familyId } = {}) {
      return builtins.filter(entry => familyId === undefined || entry.familyId === familyId);
    },
    findBuiltin(id) { return builtins.find(entry => entry.id === id); },
    createFromBuiltin(id) {
      const entry = builtins.find(item => item.id === id);
      if (!entry) throw new TemplateLibraryError('NOT_FOUND', '등록된 내장 템플릿이 없습니다.');
      return create(entry.familyId, entry.id);
    },
    listSaved,
    /** Whether the account library answered on the last listing. */
    remoteStatus: () => ({ ...remoteStatus, configured: Boolean(remote) }),
    /** Built-ins first (catalog order), then saved templates, newest first. */
    async list() {
      const saved = (await listSaved()).map(({ id, title, updatedAt, location, mine }) => ({ kind: 'saved', id, title, updatedAt, location, mine }));
      return [...builtins, ...saved];
    },
    async save({ name, document, scope = 'device' }) {
      const title = typeof name === 'string' ? name.trim() : '';
      if (!title || title.length > TITLE_LIMIT) {
        throw new TemplateLibraryError('INVALID_NAME', `템플릿 이름을 1~${TITLE_LIMIT}자로 입력해 주세요.`);
      }
      if (!TEMPLATE_SCOPES.includes(scope)) throw new TemplateLibraryError('INVALID_SCOPE', '저장 위치는 device, account, shared 중 하나입니다.');
      const markdown = codec.serialize(document);
      if (scope === 'device') {
        const saved = await store.add({ id: newId(), title, markdown, updatedAt: now() });
        return { ...saved, location: 'device', mine: true };
      }
      const account = await requireRemote();
      return fromRemote(await account.add({ id: newId(), title, markdown: forServer(markdown), visibility: scope === 'shared' ? 'shared' : 'private' }));
    },
    /** A saved template opens as an independent copy; the record never changes. */
    async open(id) {
      const record = await findSaved(id);
      const parsed = codec.parse(record.markdown);
      if (!parsed) throw new TemplateLibraryError('INVALID_RECORD', '저장한 템플릿 형식을 인식하지 못했습니다.');
      return { record, document: codec.instantiate(parsed) };
    },
    /**
     * Move a saved template to another location. account <-> shared changes
     * who can see it; device -> account/shared uploads it and then removes the
     * device copy. Server templates are not pulled back to one device, since
     * other devices of the same creator may be using them.
     */
    async changeScope(id, scope) {
      if (!TEMPLATE_SCOPES.includes(scope)) throw new TemplateLibraryError('INVALID_SCOPE', '저장 위치는 device, account, shared 중 하나입니다.');
      if (builtins.some(entry => entry.id === id)) {
        throw new TemplateLibraryError('READ_ONLY', '기본 제공 템플릿은 옮길 수 없습니다.');
      }
      const record = await findSaved(id);
      if (record.location === scope) return record;
      const visibility = scope === 'shared' ? 'shared' : 'private';
      if (record.location === 'device') {
        const account = await requireRemote();
        const moved = fromRemote(await account.add({ id: newId(), title: record.title, markdown: forServer(record.markdown), visibility }));
        await store.remove(id);
        return moved;
      }
      if (!record.mine) throw new TemplateLibraryError('READ_ONLY', '다른 제작자가 공유한 템플릿은 옮길 수 없습니다.');
      if (scope === 'device') {
        throw new TemplateLibraryError('INVALID_SCOPE', '계정 템플릿은 이 기기로 옮길 수 없습니다. 다른 기기에서도 쓰고 있을 수 있습니다.');
      }
      return fromRemote(await (await requireRemote()).update(id, { visibility }));
    },
    async remove(id) {
      if (builtins.some(entry => entry.id === id)) {
        throw new TemplateLibraryError('READ_ONLY', '기본 제공 템플릿은 삭제할 수 없습니다.');
      }
      const record = await findSaved(id);
      if (record.location === 'device') await store.remove(id);
      else {
        if (!record.mine) throw new TemplateLibraryError('READ_ONLY', '다른 제작자가 공유한 템플릿은 삭제할 수 없습니다.');
        await remote.remove(id);
      }
      return record;
    },
  };
}
